import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import type { RootState } from '@/redux/store'
import type { UserJwtData } from './types'
import { getUserRightsPerRole } from './userRights'

/**
 * Check if a JWT token's `exp` claim is in the past.
 * Returns false for malformed tokens (fail-open to avoid blocking auth flow).
 */
export const isTokenExpired = (token: string): boolean => {
  if (!token) return false
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (typeof payload.exp !== 'number') return false
    return payload.exp < Date.now() / 1000
  } catch {
    return false
  }
}

interface OneTrustState {
  open: boolean
}

export interface AuthState {
  token: string
  tokenData: UserJwtData | null
  isAuthenticated: boolean
  isFirstLogin: boolean
  isOidc: boolean
  userRights: UserRightsFromServer
  _debugUserRights: {
    useDebug: boolean
  } & UserRightsFromServer
  oneTrustState: OneTrustState
}

type UserRightsFromServer = {
  isAdmin: boolean
  isProvider: boolean
  isCatEmployee: boolean
  delegateRights: string[]
}

const initialState: AuthState = {
  token: '',
  tokenData: null,
  isAuthenticated: false,
  isFirstLogin: false,
  isOidc: false,
  userRights: {
    isAdmin: false,
    isProvider: false,
    isCatEmployee: false,
    delegateRights: [],
  },
  _debugUserRights: {
    useDebug: false,
    isAdmin: false,
    isProvider: false,
    isCatEmployee: false,
    delegateRights: [],
  },
  oneTrustState: { open: false },
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      if (action.payload && !isTokenExpired(action.payload)) {
        localStorage.setItem('token', action.payload)
      }
      state.token = action.payload
    },
    setAuthentication: (state, action: PayloadAction<boolean>) => {
      const isAuthenticated = action.payload
      if (isAuthenticated && state.token !== '') {
        state.isAuthenticated = true
      } else {
        state.isAuthenticated = false
        state.token = ''
        localStorage.removeItem('token')
      }
    },
    setCwsDirect: (
      state,
      action: PayloadAction<
        Partial<{ isFirstLogin: boolean; isOidc: boolean }>
      >,
    ) => {
      state.isFirstLogin = action.payload.isFirstLogin ?? state.isFirstLogin
      state.isOidc = action.payload.isOidc ?? state.isOidc
    },
    setUserRights: (state, action: PayloadAction<UserRightsFromServer>) => {
      if (state._debugUserRights.useDebug) {
        state.userRights.isAdmin = state._debugUserRights.isAdmin
        state.userRights.isProvider = state._debugUserRights.isProvider
        state.userRights.isCatEmployee = state._debugUserRights.isCatEmployee
        state.userRights.delegateRights = state._debugUserRights.delegateRights
      } else {
        state.userRights.isAdmin = action.payload.isAdmin
        state.userRights.isProvider = action.payload.isProvider
        state.userRights.isCatEmployee = action.payload.isCatEmployee
        state.userRights.delegateRights = action.payload.delegateRights
      }
    },
    _setDebugUserRights: (
      state,
      action: PayloadAction<UserRightsFromServer & { useDebug: boolean }>,
    ) => {
      const { useDebug, isAdmin, isProvider, isCatEmployee, delegateRights } =
        action.payload
      state._debugUserRights.useDebug = useDebug
      state._debugUserRights.isAdmin = isAdmin
      state._debugUserRights.isProvider = isProvider
      state._debugUserRights.isCatEmployee = isCatEmployee
      state._debugUserRights.delegateRights = delegateRights
    },
    setOneTrustState: (
      state,
      action: PayloadAction<Partial<OneTrustState>>,
    ) => {
      state.oneTrustState = { ...state.oneTrustState, ...action.payload }
    },
  },
})

export const {
  setToken,
  setAuthentication,
  setCwsDirect,
  setUserRights,
  setOneTrustState,
} = authSlice.actions

const { _setDebugUserRights } = authSlice.actions
export const debugActions = { _setDebugUserRights }

export const getAuthState = (state: RootState) => state.reducer.auth

const selectUserRights = createSelector(getAuthState, (state) => {
  return state.userRights
})

const selectUserRole = createSelector(getAuthState, (state) => {
  if (state.userRights.isAdmin) return 'admin'
  if (state.userRights.isProvider) return 'provider'
  if (state.userRights.isCatEmployee) return 'catEmployee'

  return 'user'
})

const selectUserRoleRights = createSelector(selectUserRole, (role) =>
  getUserRightsPerRole(role),
)

const selectUserTokenData = createSelector(getAuthState, (state) => {
  if (!state.token) return null
  try {
    return state.token
      .split('.', 2)
      .reduce(
        (obj, fragment) => ({ ...JSON.parse(atob(fragment)), ...obj }),
        {},
      ) as UserJwtData
  } catch (e) {
    console.error('Could not decode JWT claims.', e)
    return null
  }
})

const selectUserDelegates = createSelector(
  getAuthState,
  (state) => state.userRights.delegateRights,
)

export const selectors = {
  selectUserRights,
  selectUserRole,
  selectUserRoleRights,
  selectUserTokenData,
  selectUserDelegates,
}

export default authSlice.reducer
